import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAdminQuizzes } from '../../hooks/useAdminQuizzes';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiEdit, FiTrash2, FiHelpCircle, FiCheck, FiX } = FiIcons;

const QuizManager = ({ lessonId }) => {
  const { quiz, questions, createQuiz, updateQuiz, deleteQuiz, createQuestion, updateQuestion, deleteQuestion } = useAdminQuizzes(lessonId);
  const [showQuizForm, setShowQuizForm] = useState(false);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [quizData, setQuizData] = useState({ title: '', is_required: false });
  const [questionData, setQuestionData] = useState({
    type: 'mc',
    prompt: '',
    choices: ['', '', '', ''],
    answer: '',
    explanation: ''
  });

  const handleCreateQuiz = async () => {
    await createQuiz(lessonId, quizData);
    setShowQuizForm(false);
    setQuizData({ title: '', is_required: false });
  };

  const handleCreateQuestion = async () => {
    const orderIndex = questions.length;
    await createQuestion(quiz.id, {
      ...questionData,
      choices: questionData.type === 'mc' ? questionData.choices.filter(c => c.trim()) : [],
      order_index: orderIndex
    });
    setShowQuestionForm(false);
    resetQuestionForm();
  };

  const handleUpdateQuestion = async () => {
    await updateQuestion(editingQuestion, {
      ...questionData,
      choices: questionData.type === 'mc' ? questionData.choices.filter(c => c.trim()) : []
    });
    setEditingQuestion(null);
    resetQuestionForm();
  };

  const resetQuestionForm = () => {
    setQuestionData({
      type: 'mc',
      prompt: '',
      choices: ['', '', '', ''],
      answer: '',
      explanation: ''
    });
  };

  const startEditQuestion = (question) => {
    setEditingQuestion(question.id);
    setQuestionData({
      type: question.type,
      prompt: question.prompt,
      choices: question.type === 'mc' ? [...question.choices, '', '', '', ''].slice(0, 4) : ['', '', '', ''],
      answer: question.answer,
      explanation: question.explanation || ''
    });
    setShowQuestionForm(true);
  };

  return (
    <div className="space-y-4">
      {!quiz ? (
        <div className="text-center py-8">
          <SafeIcon icon={FiHelpCircle} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">No Quiz Created</h4>
          <p className="text-gray-500 mb-4">Add a quiz to test student knowledge</p>
          <button
            type="button"
            onClick={() => setShowQuizForm(true)}
            className="bg-nutrition-600 hover:bg-nutrition-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
          >
            Create Quiz
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Quiz Header */}
          <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900">{quiz.title}</h4>
              <p className="text-sm text-gray-600">
                {questions.length} questions • {quiz.is_required ? 'Required' : 'Optional'}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={() => setShowQuestionForm(true)}
                className="text-nutrition-600 hover:text-nutrition-700 font-medium flex items-center space-x-1"
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4" />
                <span>Add Question</span>
              </button>
              <button
                type="button"
                onClick={() => deleteQuiz(quiz.id)}
                className="text-red-600 hover:text-red-700 font-medium"
              >
                Delete Quiz
              </button>
            </div>
          </div>

          {/* Questions List */}
          {questions.length === 0 ? (
            <div className="text-center py-6 text-gray-500">
              <p className="text-sm">No questions added yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {questions.map((question, index) => (
                <div key={question.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-sm font-medium text-gray-500">Q{index + 1}</span>
                        <span className={`px-2 py-1 text-xs rounded ${
                          question.type === 'mc' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                        }`}>
                          {question.type === 'mc' ? 'Multiple Choice' : 'Short Response'}
                        </span>
                      </div>
                      <p className="font-medium text-gray-900 mb-2">{question.prompt}</p>
                      
                      {question.type === 'mc' && question.choices && (
                        <div className="space-y-1">
                          {question.choices.map((choice, choiceIndex) => (
                            <div key={choiceIndex} className={`flex items-center space-x-2 text-sm ${
                              choice === question.answer ? 'text-green-600 font-medium' : 'text-gray-600'
                            }`}>
                              <SafeIcon icon={choice === question.answer ? FiCheck : FiX} className="w-3 h-3" />
                              <span>{choice}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {question.type === 'sr' && (
                        <p className="text-sm text-green-600 font-medium">
                          Answer: {question.answer}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => startEditQuestion(question)}
                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                      >
                        <SafeIcon icon={FiEdit} className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteQuestion(question.id)}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Quiz Creation Form */}
      <AnimatePresence>
        {showQuizForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="border border-gray-200 rounded-lg p-4 bg-gray-50"
          >
            <h4 className="font-medium text-gray-900 mb-3">Create Quiz</h4>
            <div className="space-y-3">
              <input
                type="text"
                value={quizData.title}
                onChange={(e) => setQuizData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Quiz title..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
              />
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={quizData.is_required}
                  onChange={(e) => setQuizData(prev => ({ ...prev, is_required: e.target.checked }))}
                  className="h-4 w-4 text-nutrition-600 focus:ring-nutrition-500 border-gray-300 rounded"
                />
                <label className="text-sm text-gray-700">Required (must pass to complete lesson)</label>
              </div>
              
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowQuizForm(false)}
                  className="px-3 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreateQuiz}
                  className="bg-nutrition-600 hover:bg-nutrition-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
                >
                  Create Quiz
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Question Form */}
      <AnimatePresence>
        {showQuestionForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="border border-gray-200 rounded-lg p-4 bg-gray-50"
          >
            <h4 className="font-medium text-gray-900 mb-3">
              {editingQuestion ? 'Edit Question' : 'Add Question'}
            </h4>
            
            <div className="space-y-4">
              {/* Question Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Question Type</label>
                <select
                  value={questionData.type}
                  onChange={(e) => setQuestionData(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                >
                  <option value="mc">Multiple Choice</option>
                  <option value="sr">Short Response</option>
                </select>
              </div>

              {/* Question Prompt */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Question</label>
                <textarea
                  value={questionData.prompt}
                  onChange={(e) => setQuestionData(prev => ({ ...prev, prompt: e.target.value }))}
                  placeholder="Enter your question..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                  rows={3}
                />
              </div>

              {/* Multiple Choice Options */}
              {questionData.type === 'mc' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Answer Choices</label>
                  <div className="space-y-2">
                    {questionData.choices.map((choice, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="correct_answer"
                          checked={questionData.answer === choice}
                          onChange={() => setQuestionData(prev => ({ ...prev, answer: choice }))}
                          className="text-nutrition-600 focus:ring-nutrition-500"
                        />
                        <input
                          type="text"
                          value={choice}
                          onChange={(e) => {
                            const newChoices = [...questionData.choices];
                            newChoices[index] = e.target.value;
                            setQuestionData(prev => ({ ...prev, choices: newChoices }));
                          }}
                          placeholder={`Choice ${index + 1}`}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                        />
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Select the radio button for the correct answer</p>
                </div>
              )}

              {/* Short Response Answer */}
              {questionData.type === 'sr' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Correct Answer</label>
                  <input
                    type="text"
                    value={questionData.answer}
                    onChange={(e) => setQuestionData(prev => ({ ...prev, answer: e.target.value }))}
                    placeholder="Enter the correct answer..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                  />
                </div>
              )}

              {/* Explanation */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Explanation (Optional)</label>
                <textarea
                  value={questionData.explanation}
                  onChange={(e) => setQuestionData(prev => ({ ...prev, explanation: e.target.value }))}
                  placeholder="Explain why this is the correct answer..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                  rows={2}
                />
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowQuestionForm(false);
                    setEditingQuestion(null);
                    resetQuestionForm();
                  }}
                  className="px-3 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={editingQuestion ? handleUpdateQuestion : handleCreateQuestion}
                  className="bg-nutrition-600 hover:bg-nutrition-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
                >
                  {editingQuestion ? 'Update Question' : 'Add Question'}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default QuizManager;